'use client';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ReactNode } from 'react';

interface ReCaptchaProviderProps {
  children: ReactNode;
}

/**
 * reCAPTCHA v3 provider.
 *
 * The wrapped library registers an internal client tied to the
 * `reCaptchaKey` value used at first render. Earlier versions of this
 * file deferred mounting via a `shouldLoad` flag and rendered the
 * provider with `reCaptchaKey=""` until the browser went idle, then
 * swapped in the real key. That swap does NOT cause the library to
 * re-register, so `useGoogleReCaptcha().executeRecaptcha(action)` ends
 * up calling `grecaptcha.execute("", { action })` and fails with
 * `Invalid reCAPTCHA client id: ""` — surfacing as a 400 from the
 * forms API because the client never produces a token.
 *
 * Always render with the real key. The script tag still uses
 * `async`/`defer` so it stays off the document parse path.
 */
export function ReCaptchaProvider({ children }: ReCaptchaProviderProps) {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
}
